/**
 * TOASTS (B8): "Link copied", "The link couldn't be copied. Select it below."
 * and "The drive stopped where you took over." with Resume.
 *
 * §A.2: centred in the safe area, bottom 112, h 48. §E.9: in 420 settle
 * (fy 14), out 294 leave. One toast at a time; a new one replaces the old.
 * The timer pauses while the pointer or focus is on the toast, so "Resume"
 * never slips away from someone reaching for it.
 *
 * Rendered through a portal into <body> (the card clips its overflow and the
 * mobile sheet is transformed). Several hosts may mount (card, drive HUD);
 * only the first one renders.
 */
import { useEffect, useLayoutEffect, useRef, useState, useSyncExternalStore, type CSSProperties } from "react";
import { createPortal } from "react-dom";
import { Button, Icon, cx, useReducedMotion, type IconName } from "@/design";
import { useApp } from "@/store/store";
import { LEAVE_MS, REDUCED_MS } from "./cardState";

export interface ToastSpec {
  /** Replaces or hides a toast of the same id ("share", "drive"). */
  id: string;
  text: string;
  icon?: IconName;
  action?: { label: string; onClick: () => void };
  /** A read-only field with a link to copy by hand (share.failed). */
  link?: string;
  ms: number;
}

interface Current { spec: ToastSpec; key: number }

let current: Current | null = null;
let seq = 0;
const subs = new Set<() => void>();
const emit = () => {
  for (const f of [...subs]) f();
};
const subscribe = (cb: () => void) => {
  subs.add(cb);
  return () => {
    subs.delete(cb);
  };
};
const getCurrent = (): Current | null => current;

export function showToast(spec: ToastSpec): void {
  current = { spec, key: ++seq };
  emit();
}

/** Hide the toast (only the one with this id, when given). */
export function hideToast(id?: string): void {
  if (!current || (id && current.spec.id !== id)) return;
  current = null;
  emit();
}

/* ── one host renders ─────────────────────────────────────────────── */

const hosts: number[] = [];
let hostSeq = 0;
const hostSubs = new Set<() => void>();
const getFirstHost = (): number | undefined => hosts[0];
const subscribeHosts = (cb: () => void) => {
  hostSubs.add(cb);
  return () => {
    hostSubs.delete(cb);
  };
};

function useHostClaim(): boolean {
  const [token] = useState(() => ++hostSeq);
  useEffect(() => {
    hosts.push(token);
    for (const f of [...hostSubs]) f();
    return () => {
      const i = hosts.indexOf(token);
      if (i >= 0) hosts.splice(i, 1);
      for (const f of [...hostSubs]) f();
    };
  }, [token]);
  return useSyncExternalStore(subscribeHosts, getFirstHost, getFirstHost) === token;
}

/* ── host ─────────────────────────────────────────────────────────── */

type Shown = { c: Current; leaving: boolean };

export function ToastHost() {
  const mine = useHostClaim();
  const cur = useSyncExternalStore(subscribe, getCurrent, getCurrent);
  const layout = useApp((s) => s.layout);
  const kiosk = useApp((s) => s.kiosk) || layout === "kiosk";
  const iLeft = useApp((s) => s.insets.left);
  const iRight = useApp((s) => s.insets.right);
  const iBottom = useApp((s) => s.insets.bottom);
  const reduced = useReducedMotion();
  const [shown, setShown] = useState<Shown | null>(null);
  const [lit, setLit] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hot = useRef(false);

  // current → shown (with a leave for the one that goes)
  useEffect(() => {
    if (cur) {
      setShown({ c: cur, leaving: false });
      return undefined;
    }
    setShown((s) => (s && !s.leaving ? { ...s, leaving: true } : s));
    const t = setTimeout(() => setShown((s) => (s && s.leaving ? null : s)), reduced ? REDUCED_MS : LEAVE_MS);
    return () => clearTimeout(t);
  }, [cur, reduced]);

  // reduced motion: a 160 ms fade (a transition, which the reduced rule keeps)
  const shownKey = shown?.c.key;
  useLayoutEffect(() => {
    setLit(false);
    if (shownKey == null) return undefined;
    const r = requestAnimationFrame(() => setLit(true));
    return () => cancelAnimationFrame(r);
  }, [shownKey]);

  // auto-dismiss, paused while hovered or focused
  const arm = (ms: number) => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    const c = getCurrent();
    if (!c) return;
    const key = c.key;
    timer.current = setTimeout(() => {
      if (!hot.current && getCurrent()?.key === key) hideToast();
    }, ms);
  };
  useEffect(() => {
    if (!cur) return undefined;
    hot.current = false;
    arm(cur.spec.ms);
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
    // arm reads the module state; it only needs to run when the toast changes
  }, [cur]);

  if (!mine || !shown || typeof document === "undefined") return null;
  const { spec } = shown.c;
  const wrap: CSSProperties = layout === "mobile"
    ? { bottom: iBottom > 0 ? iBottom - 4 : undefined }
    : { left: iLeft > 0 ? Math.max(12, iLeft - 32) : undefined, right: iRight > 0 ? Math.max(12, iRight - 32) : undefined };
  const calm = () => {
    hot.current = true;
    if (timer.current) clearTimeout(timer.current);
  };
  const release = () => {
    hot.current = false;
    arm(2400);
  };
  const motion = reduced ? cx("ld-loc-card-fade", lit && !shown.leaving && "is-on") : shown.leaving ? "is-leaving" : "ld-in";
  return createPortal(
    <div className="ld ld-loc-card-layer">
      <div className={cx("ld-loc-card-toastwrap", layout === "mobile" && "ld-loc-card-toastwrap--mobile", kiosk && "ld-loc-card-toastwrap--kiosk")} style={wrap}>
        <div
          key={shown.c.key}
          className={cx("ld-loc-card-toast ld-glass", motion, !spec.action && !spec.link && "is-plain")}
          onPointerEnter={calm}
          onPointerLeave={release}
          onFocus={calm}
          onBlur={release}
        >
          {spec.icon && <Icon name={spec.icon} size={18} />}
          <div className="ld-loc-card-toast__col">
            <span>{spec.text}</span>
            {spec.link && (
              <input
                className="ld-loc-card-toast__link"
                readOnly
                value={spec.link}
                aria-label="Link to this place"
                onFocus={(e) => e.currentTarget.select()}
                autoFocus
              />
            )}
          </div>
          {spec.action && (
            <Button
              variant="glass"
              size="sm"
              onClick={() => {
                spec.action?.onClick();
                hideToast(spec.id);
              }}
            >
              {spec.action.label}
            </Button>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
